import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {ArrowLeft, ClockCounterClockwise, MagnifyingGlass, PencilSimple, Target,
        WarningCircle} from '../../icons';
import {Spinner} from '../../Spinner';
import {ClientPanelApi} from '../../../api/clientPanelApi';
import {AccessService} from '../../../utils/AccessService';
import {useFicha21} from '../../../context/UserModulesContext';
import {ClientTabs} from '../ClientTabs';
import {lineWizardEntryPath} from '../../line_wizard/lineWizardDraft';
import {NewLinePathModal} from '../../onboarding/path_zero/NewLinePathModal';
import {ChannelLogo} from '../../channels/ChannelLogo';
import {CHANNEL_INFO_BY_CHANNEL, FACEBOOK_CHANNEL} from '../../channels/channels';
import {LineGoalChip, LineProjection, LineSpendBar, lineIsFinished} from './LineVisuals';
import {lineDefinition, lineDefinitionMore} from './lineDefinition';
import {HELP_LINE_DEFINITION, LinkToArticle} from '../../help_center/LinkToArticle';
import {LineEditDrawer} from './LineEditDrawer';
import {LineGoalModal} from './LineGoalModal';
import {LineActionsMenu} from './LineActionsMenu';
import {LineManageConfirmModal, useLineManageConfirm} from './useLineManageConfirm';
import {anchorAttr} from '../../../guides/anchors';
import {
    currentMonthLabel,
    dayOfPeriodLabel,
    formatCompactMoney,
    formatFullMoney,
    parsePanelDate,
    spendPct,
} from './panelFormatters';
import {
    FILTER_FROM_PLAN,
    FILTER_INACTIVE,
    FILTER_NO_BUDGET,
    FILTER_NO_GOAL,
    FILTER_OFF_PACE,
    GOAL_PARAM,
    LINE_FILTERS,
    SORT_BUDGET,
    SORT_NAME,
    SORT_SEVERITY,
    SORT_SPEND,
    filterCount,
    lineHasBudget,
    visibleLines,
} from './linesViewLogic';
import './LinesView.scss';

// GOTCHA: Meta se representa como 'FACEBOOK' en todo el FE (`channels.js`). Un 'META' crudo cae por
// todos los `if` de ChannelLogo y no renderiza NADA, sin error.
function normalizeChannel(channel) {
    return channel === 'META' ? FACEBOOK_CHANNEL : channel;
}

// `dayOfPeriodLabel` es puro y no conoce el copy: se le pasa un `t` envuelto con el default para
// que la etiqueta nunca muestre la key cruda (la clave ya vive en es/en.json; el default es la red).
function withDayDefault(t) {
    return (key, params) => t(key, {...params, defaultValue: 'día {{day}} de {{days}}'});
}

// "12/07" — extremo de un período propio. `es-AR` fijo, como todos los números del panel.
function shortDate(value) {
    const date = parsePanelDate(value);
    if (!date) return null;
    return date.toLocaleDateString('es-AR', {day: '2-digit', month: '2-digit'});
}

const FILTER_LABELS = {
    [FILTER_OFF_PACE]: ['client_panel.lines_view_filter_off_pace', 'Fuera de ritmo'],
    [FILTER_NO_BUDGET]: ['client_panel.lines_view_filter_no_budget', 'Sin presupuesto'],
    [FILTER_NO_GOAL]: ['client_panel.lines_view_filter_no_goal', 'Sin objetivo'],
    [FILTER_FROM_PLAN]: ['client_panel.lines_view_filter_from_plan', 'Del plan'],
    // «Desactivadas» y no «Inactivas» (card XLWKaxjN): la acción se llama «Desactivar», y con
    // otro nombre en el filtro Pablo no encontró la línea que acababa de desactivar. El valor de URL
    // (`inactivas`) no cambia: es el destino de los links del Panel.
    [FILTER_INACTIVE]: ['client_panel.lines_view_filter_inactive', 'Desactivadas'],
};

/**
 * Vista de TODAS las líneas de control del cliente (`/client/:id/lines`) — el reemplazo de la
 * pestaña Monitoreo para la ficha 2.1 (card gtAL5axI). Es una vista del Panel, no otra pestaña:
 * misma fuente de datos (el agregado `GET client/<id>/panel/`, que trae todas las líneas activas
 * sin paginar) y los mismos objetos visuales que la card de líneas (`LineVisuals`).
 *
 * Los filtros viven en la URL (`?filtro=`, `?q=`) porque son el DESTINO de los CTAs del Panel:
 * "Definir objetivos" aterriza en las líneas sin objetivo, no frente a las 78 filas.
 *
 * El orden por defecto NO se recalcula acá: el backend manda CRITIC → BAD → sin dato → OK, la misma
 * unidad de veredicto que dispara las alertas. Los otros tres órdenes son elecciones explícitas del
 * usuario.
 *
 * Las ACTIVAS son la lista principal (`lines`); las desactivadas viajan aparte
 * (`inactive_lines`, filas livianas sin números) y entran por el chip «Inactivas», que CAMBIA la
 * lista en vez de filtrarla — así los conteos de KPIs y chips no se contaminan. Esta vista es la
 * única lista de inactivas en 2.1: reactivar/eliminar viven en el drawer.
 */
export function LinesView({clientId, history}) {
    const {t} = useTranslation();
    const {mediaPlanEnabled, modules, clientEconomicsEnabled} = useFicha21();

    const [panel, setPanel] = useState(null);
    const [panelFailed, setPanelFailed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [reloadToken, setReloadToken] = useState(0);
    const refetchPanel = useCallback(() => setReloadToken((token) => token + 1), []);
    // Las acciones del menú `⋮` de la fila. El mismo hook que el Panel: son la misma pantalla
    // contestando la misma pregunta desde otra densidad, y dos copias divergen (la rama de
    // «vincular las cuentas y reactivar» es justo la que se olvida en la segunda).
    const lineManage = useLineManageConfirm(refetchPanel);

    // El filtro y la búsqueda son estados de URL (compartibles, y destino de los CTAs). El orden es
    // una preferencia de lectura del momento: vive en el estado local.
    const [searchParams, setSearchParams] = useSearchParams();
    const filter = searchParams.get('filtro');
    const query = searchParams.get('q') || '';
    const [sort, setSort] = useState(SORT_SEVERITY);

    // Qué línea está abierta en el drawer de edición (paso 2 de la card). Se guarda el ID y no el
    // objeto: después de guardar, el refetch trae la línea actualizada y el drawer la ve fresca.
    const [editingId, setEditingId] = useState(null);
    // A qué parte del drawer se entró. `'manage'` = al gesto de desactivar/eliminar, que vive al
    // pie del formulario y sin esto había que buscarlo scrolleando (card IysOiDWB).
    const [editingFocus, setEditingFocus] = useState(null);
    // «Definir objetivo» abre SÓLO el modal de objetivo, no el drawer (card 6zUFUONB).
    const [goalLineId, setGoalLineId] = useState(null);
    const closeGoalModal = useCallback(() => setGoalLineId(null), []);

    // Las líneas editadas desde esta vista, que siguen a la vista aunque ya no pasen el filtro (card
    // RVO9leLh, ver `visibleLines`). Valen mientras no cambien el filtro ni la búsqueda: cualquier
    // cambio las suelta, también si después se vuelve al mismo filtro. Se sueltan en el render (el
    // patrón de React para ajustar estado cuando cambia una entrada), no en un efecto, que las
    // dejaría un render de más bajo el filtro nuevo.
    const viewKey = `${filter || ''}|${query}`;
    const [kept, setKept] = useState({viewKey, ids: new Set()});
    if (kept.viewKey !== viewKey) setKept({viewKey, ids: new Set()});
    const keptIds = kept.viewKey === viewKey ? kept.ids : null;
    const keepVisible = useCallback((lineId) => {
        if (lineId === null || lineId === undefined) return;
        setKept((prev) => {
            const ids = new Set(prev.viewKey === viewKey ? prev.ids : []);
            ids.add(lineId);
            return {viewKey, ids};
        });
    }, [viewKey]);
    // Guardar desde el modal de objetivo o desde el drawer: la línea queda donde estaba, con su
    // dato nuevo, en vez de desaparecer bajo el filtro que la trajo (el deep link del Listado
    // aterriza en `?filtro=sin-objetivo`). Se prefirió esto a sacar el filtro de la URL: con cinco
    // líneas sin objetivo, la lista es la tarea pendiente, y soltarla después de la primera dejaba
    // al usuario frente a todas las líneas del cliente.
    const handleLineSaved = useCallback((lineId) => {
        keepVisible(lineId);
        refetchPanel();
    }, [keepVisible, refetchPanel]);

    // Cambiar de cliente sin remontar (el selector del topbar navega de /client/5/lines a
    // /client/7/lines): sin esto la tabla muestra al cliente anterior mientras llega el agregado.
    /**
     * Cambiar de CLIENTE con el drawer abierto (C-panel-02 del review).
     *
     * El drawer no se desmontaba solo: `editingLineId` sobrevivía al cambio, y como la línea no
     * aparece en el panel nuevo, `editingLine` daba `null` y React lo sacaba de pantalla **sin
     * pasar por `requestClose`**. Con presupuesto u objetivo sucio, lo tipeado se iba al piso sin
     * toast ni modal.
     *
     * No se usa el guard de descarte a propósito: acá la navegación YA ocurrió (este efecto corre
     * después), así que «seguir editando» dejaría al usuario sobre una línea de un cliente que ya
     * dejó. Lo único honesto es cerrar y decir que se perdió.
     */
    useEffect(() => {
        if (drawerRef.current && drawerRef.current.hasUnsavedChanges && drawerRef.current.hasUnsavedChanges()) {
            toast.info(t('client_panel.line_drawer_discarded_on_client_change',
                'Cambiaste de cliente: se cerró la línea que estabas editando y no se guardaron los cambios.'));
        }
        closeDrawer();
        closeGoalModal();
        setPanel(null);
        setPanelFailed(false);
        // Sólo `clientId`: este efecto VACÍA la pantalla, y sumar `t` (cuya identidad cambia entre
        // renders de i18next) o la función de cierre lo haría correr sin que el cliente haya
        // cambiado — que es justo lo que el bloque de arriba dice que no puede pasar.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        ClientPanelApi.getPanel(clientId).then((data) => {
            if (cancelled) return;
            if (data && data.status === 'OK' && data.data) {
                setPanel(data.data);
                setPanelFailed(false);
            } else {
                // 404 uniforme (cliente ajeno / tier < UX_21) o error de red: no distinguimos, la
                // pantalla no puede filtrar la existencia de nada (aislamiento cross-agency).
                setPanel(null);
                setPanelFailed(true);
            }
            setLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [clientId, reloadToken]);

    const lines = useMemo(() => (panel && panel.lines) || [], [panel]);
    // Las desactivadas viajan APARTE (`inactive_lines`, filas livianas): mezclarlas en `lines`
    // contaminaría los conteos de chips y KPIs. Con un backend viejo el bloque no viaja → [].
    const inactiveLines = useMemo(() => (panel && panel.inactive_lines) || [], [panel]);
    // El vacío de «crea la primera» SOLO si no hay nada de nada (con inactivas va la tabla). Con el
    // agregado todavía en camino o caído no es un vacío: el botón de la cabecera se queda.
    const showsEmpty = !!panel && !panelFailed && lines.length === 0 && inactiveLines.length === 0;
    // El chip "Inactivas" no filtra la lista: la CAMBIA. Los sorts por monto/consumo sobre filas
    // sin números son estables (quedan en el orden alfabético del backend).
    const rows = useMemo(() => (
        filter === FILTER_INACTIVE
            ? visibleLines(inactiveLines, {query, sort})
            : visibleLines(lines, {filter, query, sort, keep: keptIds})
    ), [lines, inactiveLines, filter, query, sort, keptIds]);

    // La línea del drawer se busca en TODAS las líneas (no en las filtradas): al guardar puede
    // salir del filtro activo —cargarle presupuesto bajo `sin-presupuesto`— y el drawer no debe
    // desaparecer en la mano del usuario. La navegación ↑↓ sí corre sobre la lista filtrada.
    const editingLine = editingId === null ? null
        : (lines.find((line) => line.id === editingId)
            || inactiveLines.find((line) => line.id === editingId)
            || null);
    const editingIndex = editingId === null ? -1 : rows.findIndex((line) => line.id === editingId);
    // Abrir el drawer sobre OTRA fila teniéndolo abierto pasa por SU guard de cambios (C7 del
    // review 25/08): setear `editingId` directo remontaba el form y tiraba lo tipeado en
    // silencio. El drawer confirma (o no hay nada que confirmar) y devuelve el salto por
    // `onSwitchLine`.
    const drawerRef = useRef(null);
    const openDrawer = useCallback((lineId, focus = null) => {
        setEditingFocus(focus);
        if (editingId !== null && lineId !== editingId && drawerRef.current) {
            drawerRef.current.requestSwitch(lineId);
            return;
        }
        setEditingId(lineId);
    }, [editingId]);
    const switchDrawerLine = useCallback((lineId) => setEditingId(lineId), []);
    const closeDrawer = useCallback(() => {
        setEditingId(null);
        setEditingFocus(null);
    }, []);
    const navigateDrawer = useCallback((delta) => {
        setEditingId((currentId) => {
            const index = rows.findIndex((line) => line.id === currentId);
            const next = index < 0 ? null : rows[index + delta];
            return next ? next.id : currentId;
        });
    }, [rows]);

    // Un solo lugar arma la URL nueva: `setSearchParams` REEMPLAZA todos los params, así que hay
    // que reconstruir siempre los dos. `replace` para la búsqueda (una entrada de historial por
    // tecla no le sirve a nadie); push para los chips (volver atrás des-filtra).
    const updateParams = useCallback((nextFilter, nextQuery, {replace = false} = {}) => {
        const params = {};
        if (nextFilter) params.filtro = nextFilter;
        if (nextQuery) params.q = nextQuery;
        setSearchParams(params, {replace});
    }, [setSearchParams]);

    // `?objetivo=<id>`: el CTA «Definir objetivos» del Listado de clientes aterriza acá con el modal
    // de esa línea ya abierto (antes iba a la página completa de la línea). El param se consume
    // una vez: `updateParams` reescribe la URL sólo con filtro y búsqueda, así que volver atrás o
    // refrescar no lo reabre.
    const goalParam = searchParams.get(GOAL_PARAM);
    useEffect(() => {
        if (!goalParam || !panel) return;
        const target = lines.find((line) => String(line.id) === goalParam);
        if (target) setGoalLineId(target.id);
        updateParams(filter, query, {replace: true});
    }, [goalParam, panel, lines, filter, query, updateParams]);
    const goalLine = goalLineId === null ? null
        : (lines.find((line) => line.id === goalLineId) || null);

    const toggleFilter = (key) => updateParams(filter === key ? null : key, query);
    const changeQuery = (value) => updateParams(filter, value, {replace: true});
    const clearAll = () => updateParams(null, null);

    // "Nueva línea de control" pasa primero por el paso 0, que pregunta CÓMO armarla: desde las
    // cuentas ya conectadas, desde un media plan o a mano. El camino a mano es el wizard de 4 pasos
    // (card CvdQbnl2), el mismo destino que el botón del Panel: antes uno abría el modal viejo y el
    // otro pasaba por acá. Si el paso 0 no tiene nada que ofrecer, se saltea solo y va al wizard.
    const [showPathZero, setShowPathZero] = useState(false);
    const openLineWizard = useCallback(() => {
        history.push(lineWizardEntryPath(clientId));
    }, [history, clientId]);
    const handleNewLine = useCallback(() => setShowPathZero(true), []);

    // Mismo gate que la card de líneas del Panel: capability + módulo MCP + el bloque del payload.
    const hasMcp = (modules || []).includes(AccessService.MODULE_DASHBO_MCP);
    const showMediaPlan = mediaPlanEnabled === true && hasMcp
        && (!panel || ((panel.media_plan || {}).enabled !== false));
    // El modal de objetivo suma la sugerencia desde el margen con el MISMO gate que el Panel.
    const economy = clientEconomicsEnabled === true && panel && panel.economy
        && panel.economy.enabled !== false ? panel.economy : null;

    const kpis = (panel && panel.kpis) || {};
    const month = kpis.month || {};
    const dayLabel = dayOfPeriodLabel(month.day, month.days_in_month, withDayDefault(t));
    const currency = panel && panel.client && panel.client.currency;

    // ------------------------------------------------------------------ header ------------------

    // La línea de contexto debajo del título. Presupuesto e invertido salen de `kpis` — los MISMOS
    // números que el Panel (deduplicados por el backend), nunca la suma de las filas: un cliente
    // con líneas superpuestas o en varias monedas haría mentir a la suma.
    const renderMeta = () => {
        const parts = [];
        if (panel && panel.client && panel.client.name) parts.push(panel.client.name);
        parts.push(dayLabel ? `${currentMonthLabel()} — ${dayLabel}` : currentMonthLabel());

        const budgetTotal = formatCompactMoney(currency, (kpis.budget || {}).total);
        if (budgetTotal) {
            parts.push(t('client_panel.lines_view_meta_budget', '{{amount}} de presupuesto', {
                amount: budgetTotal, interpolation: {escapeValue: false},
            }));
        }
        const invested = formatCompactMoney(currency, (kpis.investment || {}).spend_to_date);
        if (invested) {
            parts.push(t('client_panel.lines_view_meta_spend', '{{amount}} invertidos', {
                amount: invested, interpolation: {escapeValue: false},
            }));
        }
        return <div className="plv-meta">{parts.join(' · ')}</div>;
    };

    const renderHeader = () => (
        <header className="plv-head">
            <div className="plv-head__txt">
                <h1 className="plv-title">
                    {t('client_panel.lines_view_title', 'Líneas de seguimiento')}
                    {lines.length > 0 && <span className="plv-title__count">{` · ${lines.length}`}</span>}
                </h1>
                {renderMeta()}
            </div>
            <div className="plv-head__actions">
                {/* Con la planilla ya vinculada, «Sincronizar» prometería algo hecho (C-LINEAS-05 del
                    review). Acá no hay franja del plan como en el Panel, así que el botón no se
                    esconde: pasa a ser la puerta al media plan, con el mismo rótulo que la franja. */}
                {showMediaPlan && (
                    <Link className="pnl-btn pnl-btn--ghost" to={`/client/${clientId}/media-plan`}>
                        {panel && panel.media_plan && panel.media_plan.connected
                            ? t('client_panel.media_plan_open', 'Abrir media plan')
                            : t('client_panel.lines_import_media_plan', 'Sincronizar con planilla de media plan')}
                    </Link>
                )}
                {/* Sin ninguna línea, el vacío ya ofrece «Nueva línea de seguimiento» como su única
                    acción: dos primarios naranjas en la misma pantalla (QA-VIS-SEGFE-12). Mismo
                    criterio que la card del Panel con «Sincronizar». */}
                {!showsEmpty && (
                    <button type="button" className="pnl-btn pnl-btn--primary" onClick={handleNewLine}>
                        {t('client_panel.header_new_line', 'Nueva línea de seguimiento')}
                    </button>
                )}
            </div>
        </header>
    );

    // ------------------------------------------------------------------ toolbar -----------------

    const renderChips = () => LINE_FILTERS.map((key) => {
        const [labelKey, labelDefault] = FILTER_LABELS[key];
        // "Inactivas" cuenta SU lista (viaja aparte del payload); el resto filtra las activas.
        const count = key === FILTER_INACTIVE ? inactiveLines.length : filterCount(lines, key);
        const active = filter === key;
        // Un chip en (0) no se esconde: aparecer/desaparecer se lee como un estado del botón. Se
        // deshabilita — salvo que esté prendido, porque hay que poder apagarlo.
        return (
            <button
                key={key}
                type="button"
                className={`plv-chip${active ? ' -on' : ''}`}
                disabled={!active && count === 0}
                onClick={() => toggleFilter(key)}
            >
                {t(labelKey, labelDefault)}
                <span className="plv-chip__count">{count}</span>
            </button>
        );
    });

    const renderToolbar = () => (
        <div className="plv-toolbar">
            {/* Un <div>, no un <label>: `_forms.scss` aplica estilos GLOBALES por elemento a todo
                <label> y los `em` se componen adentro (gotcha del repo). */}
            <div className="plv-search">
                <MagnifyingGlass size={14} weight="bold"/>
                <input
                    type="text"
                    value={query}
                    placeholder={t('client_panel.lines_view_search', 'Buscar una línea…')}
                    autoComplete="off"
                    onChange={(event) => changeQuery(event.target.value)}
                />
            </div>
            <div className="plv-chips">{renderChips()}</div>
            <select
                className="plv-sort"
                value={sort}
                aria-label={t('client_panel.lines_view_sort_label', 'Ordenar las líneas')}
                onChange={(event) => setSort(event.target.value)}
            >
                <option value={SORT_SEVERITY}>
                    {t('client_panel.lines_view_sort_severity', 'Orden: las que piden atención primero')}
                </option>
                <option value={SORT_BUDGET}>{t('client_panel.lines_view_sort_budget', 'Orden: mayor presupuesto')}</option>
                <option value={SORT_SPEND}>{t('client_panel.lines_view_sort_spend', 'Orden: mayor consumo (%)')}</option>
                <option value={SORT_NAME}>{t('client_panel.lines_view_sort_name', 'Orden: alfabético')}</option>
            </select>
        </div>
    );

    // ------------------------------------------------------------------ filas -------------------

    const renderChannels = (line) => {
        const channels = (line.channels || [])
            .map(normalizeChannel)
            .filter((channel) => CHANNEL_INFO_BY_CHANNEL[channel]);
        if (!channels.length) return null;
        return (
            <span className="plv-name__chans">
                {channels.map((channel) => (
                    <ChannelLogo key={channel} channel={channel} height={16} width={16}
                                 hspace={0} vspace={0} marginLeft={0} padding={0}/>
                ))}
            </span>
        );
    };

    // El presupuesto de la fila abre el drawer de edición (paso 2): doce presupuestos son doce
    // ediciones, no doce viajes a otra pantalla. El lápiz sólo aparece en hover (LinesView.scss).
    // La guía «Mostrarme cómo» del presupuesto (Aprende Dashbo) apunta al de la primera fila ACTIVA:
    // una desactivada no tiene presupuesto que tocar.
    const renderBudgetCell = (line, guideAnchor = {}) => {
        if (!lineHasBudget(line)) {
            return (
                <button type="button" className="plv-edit -miss" onClick={() => openDrawer(line.id)} {...guideAnchor}>
                    {t('client_panel.lines_view_load_budget', 'Cargar')}
                </button>
            );
        }
        return (
            <button type="button" className="plv-edit" onClick={() => openDrawer(line.id)} {...guideAnchor}>
                {formatFullMoney(line.currency, line.budget.allocated)}
                <PencilSimple className="plv-edit__pen" size={12} weight="bold"/>
            </button>
        );
    };

    const renderConsumoCell = (line) => {
        const pct = spendPct((line.budget || {}).spend, (line.budget || {}).allocated);
        return (
            <div className="plv-consumo">
                <div className="plv-consumo__bar"><LineSpendBar line={line}/></div>
                <span className="plv-consumo__pct">{pct === null ? '—' : `${pct}%`}</span>
            </div>
        );
    };

    const renderPeriodCell = (line) => {
        const period = line.period || {};
        if (period.type !== 'TEMPORARY') {
            return t('client_panel.lines_view_period_monthly', 'Mensual');
        }
        const from = shortDate(period.from);
        const to = shortDate(period.to);
        return (
            <>
                {from && to
                    ? `${from} – ${to}`
                    : t('client_panel.lines_view_period_own', 'Período propio')}
                {/* La marca remite a la nota del pie: sin ella un rango de fechas suelto no dice
                    que el "esperado a hoy" de esa fila corre contra OTRO calendario. */}
                <span className="plv-period__mark" aria-hidden="true"> ✱</span>
            </>
        );
    };

    // El sub de la fila: cuántas campañas mide y, si nació del media plan, su origen (el badge que
    // tenía Monitoreo). En una fila inactiva no hay campañas frescas que contar: dice su estado.
    //
    // El conteo de campañas es un LINK a las fuentes de datos de la línea: qué campañas mide es
    // justamente lo que se va a editar cuando ese número no cuadra, y hasta acá el camino era
    // drawer → «Abrir la línea completa» → pestaña (tres saltos para la pantalla que el número
    // está nombrando). Pedido de fede en el smoke del 26/08.
    const renderSub = (line) => {
        const parts = [];
        if (line.from_media_plan === true) {
            parts.push(t('client_panel.lines_view_from_plan_tag', 'del media plan'));
        }
        if (line.status === 'INACTIVE') {
            parts.push(t('client_panel.lines_view_inactive_tag', 'desactivada'));
        } else {
            const campaigns = line.campaigns || {};
            parts.push(
                <Link
                    className="plv-name__sources"
                    to={`/business-unit/${line.id}/accounts`}
                    title={t('client_panel.lines_view_edit_sources', 'Editar las fuentes de datos de esta línea')}
                >
                    {t('client_panel.lines_view_campaigns', {
                        count: Number(campaigns.active_count || 0),
                        defaultValue: '{{count}} campaña activa',
                        defaultValue_other: '{{count}} campañas activas',
                    })}
                </Link>
            );
        }
        // El separador va acá y no en un `join`: las partes ya no son todas texto.
        return parts.map((part, index) => (
            <React.Fragment key={index}>{index > 0 && ' · '}{part}</React.Fragment>
        ));
    };

    // Fila de una línea DESACTIVADA (payload liviano): sin números que afirmar — no sincroniza.
    // Mismos tres gestos que la activa; el historial de sus cierres sigue siendo suyo.
    const renderInactiveRow = (line) => (
        <tr key={line.id} className="plv-row--off">
            <td>
                <div className="plv-name">
                    {renderChannels(line)}
                    <span className="plv-name__txt">
                        <Link className="plv-name__link" to={`/business-unit/${line.id}/`}>{line.name}</Link>
                        <span className="plv-name__sub">{renderSub(line)}</span>
                    </span>
                </div>
            </td>
            <td className="plv-num">—</td>
            <td>—</td>
            <td className="plv-num">—</td>
            <td>—</td>
            <td className="plv-period">—</td>
            <td className="plv-row-actions">
                {/* Reponer el lápiz acá NO es simetría decorativa: hasta el 10/09 la única puerta
                    al drawer desde una fila inactiva era el `⋮`, y al convertirlo en menú esa fila
                    se quedaba sin ninguna. El drawer ya sabe pintarse para una línea desactivada
                    (dice que no sincroniza en vez de inventarle un consumo). */}
                <button
                    type="button"
                    className="plv-hist"
                    title={t('client_panel.lines_view_edit_line', 'Editar presupuesto y objetivo')}
                    aria-label={t('client_panel.lines_view_edit_line', 'Editar presupuesto y objetivo')}
                    onClick={() => openDrawer(line.id)}
                >
                    <PencilSimple size={14} weight="bold"/>
                </button>
                <LineActionsMenu
                    line={line}
                    triggerClassName="plv-hist"
                    onDeactivate={lineManage.requestDeactivate}
                    onActivate={lineManage.requestActivate}
                    onDelete={lineManage.requestDelete}
                />
                <Link
                    className="plv-hist"
                    to={`/client/${clientId}/history?business_unit=${line.id}`}
                    title={t('client_panel.line_history', 'Ver el historial de esta línea')}
                    aria-label={t('client_panel.line_history', 'Ver el historial de esta línea')}
                >
                    <ClockCounterClockwise size={14} weight="bold"/>
                </Link>
            </td>
        </tr>
    );

    const renderRow = (line) => {
        if (line.status === 'INACTIVE') return renderInactiveRow(line);
        return (
            <tr key={line.id}>
                <td>
                    <div className="plv-name">
                        {renderChannels(line)}
                        <span className="plv-name__txt">
                            <Link className="plv-name__link" to={`/business-unit/${line.id}/`}>{line.name}</Link>
                            <span className="plv-name__sub">{renderSub(line)}</span>
                        </span>
                    </div>
                </td>
                <td className="plv-num">
                    {renderBudgetCell(line, line.id === guideLineId ? anchorAttr('lines_view_budget') : {})}
                </td>
                <td>{renderConsumoCell(line)}</td>
                <td className="plv-num">
                    <LineProjection projection={line.projection} alertLevel={line.budget_alert_level}/>
                </td>
                <td>
                    {/* La misma regla que el Panel: en una línea terminada no se define ni se edita un
                        objetivo (el período cerró); el que tenía se muestra como rótulo. */}
                    {!(lineIsFinished(line) && !(line.goal && line.goal.defined)) && (
                        <LineGoalChip goal={line.goal} lineId={line.id} floatingTip
                                      onDefine={lineIsFinished(line) ? undefined : () => setGoalLineId(line.id)}/>
                    )}
                </td>
                <td className="plv-period">{renderPeriodCell(line)}</td>
                <td className="plv-row-actions">
                    <button
                        type="button"
                        className="plv-hist"
                        title={t('client_panel.lines_view_edit_line', 'Editar presupuesto y objetivo')}
                        aria-label={t('client_panel.lines_view_edit_line', 'Editar presupuesto y objetivo')}
                        onClick={() => openDrawer(line.id)}
                    >
                        <PencilSimple size={14} weight="bold"/>
                    </button>
                    {/* Las demás acciones. Hasta el 10/09 este `⋮` abría el MISMO drawer que el
                        lápiz de al lado, con el scroll puesto en «Gestión» — dos gestos contiguos
                        y un solo destino (card yg9Z6jW0). Ahora despliega, como el `⋮` del Listado
                        v2, y desactivar y eliminar quedan a un click. */}
                    <LineActionsMenu
                        line={line}
                        triggerClassName="plv-hist"
                        onDeactivate={lineManage.requestDeactivate}
                        onActivate={lineManage.requestActivate}
                        onDelete={lineManage.requestDelete}
                    />
                    <Link
                        className="plv-hist"
                        to={`/client/${clientId}/history?business_unit=${line.id}`}
                        title={t('client_panel.line_history', 'Ver el historial de esta línea')}
                        aria-label={t('client_panel.line_history', 'Ver el historial de esta línea')}
                    >
                        <ClockCounterClockwise size={14} weight="bold"/>
                    </Link>
                </td>
            </tr>
        );
    };

    // ------------------------------------------------------------------ estados vacíos ----------

    // Cliente sin una sola línea: mismas dos vías de carga que la card del Panel.
    const renderEmpty = () => (
        <div className="pnl-empty">
            <Target size={34}/>
            <h3>{t('client_panel.lines_empty_title', 'Crea la primera línea de seguimiento')}</h3>
            {/* Qué se va a crear, con la definición única (card mFHzKeNK). */}
            <p className="pnl-empty__text">{lineDefinition(t)}</p>
            <div className="pnl-empty__more">
                <LinkToArticle article={HELP_LINE_DEFINITION} plain text={lineDefinitionMore(t)}/>
            </div>
            <div className="pnl-empty__actions">
                <button type="button" className="pnl-btn pnl-btn--primary" onClick={handleNewLine}>
                    {t('client_panel.header_new_line', 'Nueva línea de seguimiento')}
                </button>
            </div>
        </div>
    );

    // Hay líneas pero ninguna pasa el filtro/la búsqueda: se ofrece limpiar, no una tabla muda.
    const renderNoResults = () => {
        // Cero activas pero HAY inactivas (ej. se acaba de desactivar la única línea desde el
        // drawer): ni el empty de «crea la primera» (mentiría — hay líneas, apagadas) ni el
        // «ninguna coincide con el filtro» (no hay filtro). Se dice la verdad y se ofrece ir a
        // reactivarlas — en 2.1 esta vista es la ÚNICA lista de inactivas (C1 del review 25/08).
        if (!filter && !query.trim() && lines.length === 0 && inactiveLines.length > 0) {
            return (
                <div className="plv-noresults">
                    <p>{t('client_panel.lines_view_only_inactive',
                        'Este cliente no tiene líneas activas. Las desactivadas se pueden reactivar desde su filtro.')}</p>
                    <button type="button" className="pnl-btn pnl-btn--ghost pnl-btn--sm"
                            onClick={() => toggleFilter(FILTER_INACTIVE)}>
                        {t('client_panel.lines_view_see_inactive', 'Ver las desactivadas')}
                    </button>
                </div>
            );
        }
        return (
            <div className="plv-noresults">
                <p>{t('client_panel.lines_view_no_results', 'Ninguna línea coincide con el filtro o la búsqueda.')}</p>
                <button type="button" className="pnl-btn pnl-btn--ghost pnl-btn--sm" onClick={clearAll}>
                    {t('client_panel.lines_view_clear', 'Limpiar filtros')}
                </button>
            </div>
        );
    };

    const renderError = () => (
        <div className="pnl-error">
            <WarningCircle size={34}/>
            <h3>{t('client_panel.lines_view_error_title', 'No pudimos cargar las líneas')}</h3>
            <p>{t('client_panel.error_text', 'Hubo un problema al traer los datos de este cliente. Intenta de nuevo en unos segundos.')}</p>
            <button type="button" className="pnl-btn pnl-btn--primary" onClick={refetchPanel}>
                {t('client_panel.error_retry', 'Reintentar')}
            </button>
        </div>
    );

    // ------------------------------------------------------------------ tabla -------------------

    const hasOwnPeriodRows = rows.some((line) => (line.period || {}).type === 'TEMPORARY');

    const renderFoot = () => (
        <div className="plv-foot">
            <span className="plv-foot__count">
                {t('client_panel.lines_view_foot_count', '{{visible}} de {{total}} líneas', {
                    visible: rows.length,
                    // Bajo el chip "Inactivas" el universo es SU lista, no las activas.
                    total: filter === FILTER_INACTIVE ? inactiveLines.length : lines.length,
                })}
            </span>
            <span className="plv-foot__legend">
                <span className="plv-foot__mark">│</span>
                {t('client_panel.lines_legend', 'consumo esperado a hoy')}
                {dayLabel ? ` — ${dayLabel}` : ''}
            </span>
            {hasOwnPeriodRows && (
                <span className="plv-foot__own-period">
                    {t('client_panel.lines_view_own_period_note', '✱ período propio, distinto del mes calendario')}
                </span>
            )}
        </div>
    );

    const guideLineId = (rows.find((line) => line.status !== 'INACTIVE') || {}).id;

    const renderTable = () => (
        <>
            {renderToolbar()}
            {rows.length === 0 ? renderNoResults() : (
                /* El wrapper NO tiene `overflow-x: auto` por defecto: eso crearía un scrollport
                   propio y el sticky del thead dejaría de funcionar (gotcha del handoff — el
                   contenedor que scrollea es `.app-content`). Sólo desborda bajo la media query
                   angosta, donde el sticky se resigna a cambio de no romper el layout. */
                <div className="plv-tw">
                <table className="plv-table">
                    <thead>
                        <tr>
                            <th>{t('client_panel.lines_view_col_line', 'Línea')}</th>
                            <th className="plv-num">{t('client_panel.lines_view_col_budget', 'Presupuesto')}</th>
                            <th className="plv-col-consumo">{t('client_panel.lines_view_col_spend', 'Consumo')}</th>
                            <th className="plv-num">{t('client_panel.lines_view_col_projection', 'Proyección')}</th>
                            <th>{t('client_panel.lines_view_col_goal', 'Objetivo')}</th>
                            <th>{t('client_panel.lines_view_col_period', 'Período')}</th>
                            <th className="plv-col-actions" aria-hidden="true"/>
                        </tr>
                    </thead>
                    <tbody>{rows.map(renderRow)}</tbody>
                </table>
                </div>
            )}
            {renderFoot()}
        </>
    );

    const renderBody = () => {
        if (panelFailed) return renderError();
        if (loading && !panel) {
            return <div className="box-loader"><Spinner color="positive"/></div>;
        }
        return (
            <div className="box plv-card">
                {/* El empty de «crea la primera» SOLO si no hay nada de nada: con inactivas, la
                    tabla se muestra igual — el chip «Inactivas» es la única puerta para
                    reactivarlas en 2.1 (Monitoreo ya no existe acá). */}
                {showsEmpty ? renderEmpty() : renderTable()}
            </div>
        );
    };

    return (
        <section className="section-content">
            <ClientTabs clientId={clientId}/>

            <div className="pnl-screen plv-screen">
                <Link className="plv-back" to={`/client/${clientId}`}>
                    <ArrowLeft size={14} weight="bold"/>
                    {t('client_panel.lines_view_back', 'Volver al resumen del panel')}
                </Link>

                {renderHeader()}
                {renderBody()}

                {/* El drawer vive dentro del `pnl-screen` (los objetos compartidos `pnl-*` que usa
                    están scopeados ahí); su posición es fixed, así que el layout no lo afecta. */}
                {editingLine && (
                    <LineEditDrawer
                        ref={drawerRef}
                        line={editingLine}
                        clientId={clientId}
                        position={{index: editingIndex, total: rows.length}}
                        focusSection={editingFocus}
                        onClose={closeDrawer}
                        onNavigate={navigateDrawer}
                        onSwitchLine={switchDrawerLine}
                        onSaved={handleLineSaved}
                    />
                )}
            </div>

            {/* Fuera del `plv-screen` para que el modal no herede el layout de la pantalla (misma
                integración que el Panel). */}
            <LineManageConfirmModal
                action={lineManage.confirmAction}
                onYes={lineManage.confirmYes}
                onNo={lineManage.cancel}
                isSaving={lineManage.escribiendo}
            />

            {/* `key` por línea (y otra al cerrar): cada apertura nace de cero (ver el Panel). */}
            <LineGoalModal
                key={goalLineId === null ? 'closed' : goalLineId}
                line={goalLine}
                isOpen={!!goalLine}
                onClose={closeGoalModal}
                onSaved={handleLineSaved}
                onOpenFull={(lineId) => openDrawer(lineId)}
                clientId={clientId}
                economy={economy}
            />

            {showPathZero && (
                <NewLinePathModal
                    isOpen
                    toggle={() => setShowPathZero(false)}
                    clientId={clientId}
                    clientName={(panel && panel.client && panel.client.name) || ''}
                    mediaPlanAvailable={showMediaPlan}
                    onPickManual={openLineWizard}
                    onCreated={refetchPanel}
                />
            )}
        </section>
    );
}
